/** Small display helpers shared by every client (Avatar initials, Badge counts, member names). */

/** Up to two uppercase initials from a display name: "Ada Lovelace" → "AL", "ada" → "A", "" → "?". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return "?";
  const first = words[0]!;
  const last = words.length > 1 ? words[words.length - 1]! : "";
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/**
 * Avatar background tone 1–8 (`avatar.<tone>`): the sum of the name's UTF-16 code units mod 8, + 1 — identical on
 * every platform (Swift `name.utf16`, Kotlin `name.sumOf { it.code }`): "Ana 🚀" → 6.
 */
export function avatarTone(name: string): number {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return (sum % 8) + 1;
}

/** Badge text for an unread/mention count: 0 → "", 5 → "5", 120 → "99+". */
export function badgeCount(count: number, max: number = 99): string {
  if (count <= 0) return "";
  return count > max ? `${max}+` : String(count);
}

/** Human list of names: ["Ada"] → "Ada", ["Ada","Bo"] → "Ada and Bo", 4 names with limit 2 → "Ada, Bo and 2 others". */
export function joinNames(names: string[], limit: number = 3): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  if (names.length <= limit) {
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]!}`;
  }
  const rest = names.length - limit;
  return `${names.slice(0, limit).join(", ")} and ${rest} ${rest === 1 ? "other" : "others"}`;
}
